import { useId, useState } from "react";
import { ArrowDownward, ArrowUpward, Check, SortOutlined } from "@mui/icons-material";
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { ACTIVITY_COLUMNS, activityColumn, type ActivitySort } from "../activityColumns";

const SORTABLE_COLUMNS = ACTIVITY_COLUMNS.filter((column) => column.sortable);

function directionLabel(sort: ActivitySort): string {
  if (sort.column === "time") return sort.direction === "desc" ? "Newest first" : "Oldest first";
  if (activityColumn(sort.column).numeric) {
    return sort.direction === "asc" ? "Lowest first" : "Highest first";
  }
  return sort.direction === "asc" ? "A–Z" : "Z–A";
}

interface PartnerActivitySortMenuProps {
  sort: ActivitySort;
  onChange: (sort: ActivitySort) => void;
}

/** Compact column menu with independently selectable direction. */
export function PartnerActivitySortMenu({ sort, onChange }: PartnerActivitySortMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const open = Boolean(anchorEl);
  const currentLabel = `${activityColumn(sort.column).label} — ${directionLabel(sort)}`;

  return (
    <>
      <Tooltip title={`Sort: ${currentLabel}`} describeChild>
        <Button
          variant="outlined"
          size="small"
          aria-label={`Sort activity, current: ${currentLabel}`}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          startIcon={<SortOutlined />}
          endIcon={sort.direction === "asc" ? <ArrowUpward /> : <ArrowDownward />}
          sx={{ color: "text.primary", borderColor: "divider", minHeight: 44 }}
        >
          Sort: {activityColumn(sort.column).label}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          list: { id: menuId, "aria-label": "Activity sort order" },
          paper: { sx: { minWidth: 240 } },
        }}
      >
        <MenuItem disabled>Sort by</MenuItem>
        {SORTABLE_COLUMNS.map((column) => (
          <MenuItem
            key={column.id}
            role="menuitemradio"
            aria-checked={sort.column === column.id}
            selected={sort.column === column.id}
            onClick={() => {
              if (column.id !== sort.column) {
                onChange({
                  column: column.id,
                  direction: column.sortAscByDefault ? "asc" : "desc",
                });
              }
            }}
            sx={{ minHeight: 48 }}
          >
            <ListItemText>{column.label}</ListItemText>
            {sort.column === column.id && <Check fontSize="small" color="primary" sx={{ ml: 2 }} />}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled>Direction</MenuItem>
        {(["asc", "desc"] as const).map((direction) => {
          const Icon = direction === "asc" ? ArrowUpward : ArrowDownward;
          return (
            <MenuItem
              key={direction}
              role="menuitemradio"
              aria-checked={sort.direction === direction}
              selected={sort.direction === direction}
              onClick={() => {
                onChange({ ...sort, direction });
                setAnchorEl(null);
              }}
              sx={{ minHeight: 48 }}
            >
              <ListItemIcon>
                <Icon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{directionLabel({ ...sort, direction })}</ListItemText>
              {sort.direction === direction && <Check fontSize="small" color="primary" />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
